import { c as _c } from "react/compiler-runtime";
import type { Color } from '@onefootprint/design-tokens';
import type { Icon } from '@onefootprint/icons';
import { cva, cx, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
export type IconButtonSize = 'default' | 'large' | 'compact' | 'tiny';
type IconButtonColor = Color;
export type IconButtonProps = {
  'aria-label': string;
  icon: Icon;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  className?: string;
  color?: IconButtonColor;
  destructive?: boolean;
} & VariantProps<typeof iconButton> & React.ButtonHTMLAttributes<HTMLButtonElement>;
const iconButton = cva(['flex items-center justify-center box-border cursor-pointer', 'transition-[background-color,border-color,color,box-shadow,transform] duration-100 ease-out', 'active:enabled:scale-pressed-sm motion-reduce:active:enabled:scale-100'], {
  variants: {
    size: {
      large: 'size-10',
      default: 'size-8',
      compact: 'size-7',
      tiny: 'size-6'
    },
    rounded: {
      true: 'rounded-full'
    },
    variant: {
      outline: ['border border-solid border-tertiary bg-(--button-secondary-bg) shadow-(--button-secondary-shadow)', 'hover:bg-[image:linear-gradient(var(--button-secondary-hover-bg),var(--button-secondary-hover-bg))] hover:border-primary hover:shadow-(--button-secondary-hover-shadow)', 'active:bg-(--button-secondary-active-bg) active:border-primary active:shadow-(--button-secondary-active-shadow)', 'disabled:pointer-events-none disabled:bg-secondary disabled:border-tertiary disabled:shadow-(--button-secondary-disabled-shadow)'],
      ghost: ['bg-transparent border-none', 'hover:bg-hover-control active:bg-hover-control', 'disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:bg-none data-[disabled=true]:cursor-default data-[disabled=true]:opacity-50', 'data-[destructive=true]:hover:bg-error data-[destructive=true]:active:bg-error']
    },
    destructive: {
      true: ['hover:bg-error active:bg-error', 'data-[destructive=true]:border-error data-[destructive=true]:hover:border-error']
    }
  },
  compoundVariants: [{
    size: 'large',
    rounded: undefined,
    class: 'rounded-(--icon-button-border-radius)'
  }, {
    size: 'large',
    rounded: false,
    class: 'rounded-(--icon-button-border-radius)'
  }, {
    size: 'default',
    rounded: undefined,
    class: 'rounded-(--icon-button-border-radius)'
  }, {
    size: 'default',
    rounded: false,
    class: 'rounded-(--icon-button-border-radius)'
  }, {
    size: 'compact',
    rounded: undefined,
    class: 'rounded-(--icon-button-border-radius)'
  }, {
    size: 'compact',
    rounded: false,
    class: 'rounded-(--icon-button-border-radius)'
  }, {
    size: 'tiny',
    rounded: undefined,
    class: 'rounded-(--icon-button-border-radius-tiny)'
  }, {
    size: 'tiny',
    rounded: false,
    class: 'rounded-(--icon-button-border-radius-tiny)'
  },
  // Bordered like a Button, so it takes the Button's shape (a pill under rb) to sit beside one.
  {
    variant: 'outline',
    rounded: undefined,
    class: 'rounded-(--button-border-radius)!'
  }, {
    variant: 'outline',
    rounded: false,
    class: 'rounded-(--button-border-radius)!'
  }],
  defaultVariants: {
    size: 'default',
    variant: 'ghost'
  }
});
const getIconColor = (color?: IconButtonColor, destructive?: boolean, disabled?: boolean) => {
  if (destructive) {
    return 'error';
  }
  if (disabled) {
    return 'quaternary';
  }
  if (color) {
    return color;
  }
  return 'primary';
};
const handleClick = (event: React.MouseEvent<HTMLButtonElement>, onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void) => {
  event.stopPropagation();
  onClick?.(event);
};
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>((t0, ref) => {
  const $ = _c(36);
  let Icon;
  let ariaLabel;
  let className;
  let destructive;
  let disabled;
  let onClick;
  let props;
  let rounded;
  let size;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      "aria-label": ariaLabel,
      icon: Icon,
      onClick,
      size,
      variant,
      rounded,
      disabled,
      className,
      color: t1,
      destructive,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = ariaLabel;
    $[3] = className;
    $[4] = destructive;
    $[5] = disabled;
    $[6] = onClick;
    $[7] = props;
    $[8] = rounded;
    $[9] = size;
    $[10] = t1;
    $[11] = variant;
  } else {
    Icon = $[1];
    ariaLabel = $[2];
    className = $[3];
    destructive = $[4];
    disabled = $[5];
    onClick = $[6];
    props = $[7];
    rounded = $[8];
    size = $[9];
    t1 = $[10];
    variant = $[11];
  }
  const color = t1 === undefined ? "primary" : t1;
  let t2;
  if ($[12] !== className || $[13] !== destructive || $[14] !== rounded || $[15] !== size || $[16] !== variant) {
    t2 = cx(iconButton({
      size,
      variant,
      rounded,
      destructive
    }), className);
    $[12] = className;
    $[13] = destructive;
    $[14] = rounded;
    $[15] = size;
    $[16] = variant;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  let t3;
  if ($[18] !== onClick) {
    t3 = e => handleClick(e, onClick);
    $[18] = onClick;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== color || $[21] !== destructive || $[22] !== disabled) {
    t4 = getIconColor(color, destructive, disabled);
    $[20] = color;
    $[21] = destructive;
    $[22] = disabled;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  let t5;
  if ($[24] !== Icon || $[25] !== t4) {
    t5 = <Icon color={t4} />;
    $[24] = Icon;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== ariaLabel || $[28] !== destructive || $[29] !== disabled || $[30] !== props || $[31] !== ref || $[32] !== t2 || $[33] !== t3 || $[34] !== t5) {
    t6 = <button className={t2} aria-label={ariaLabel} onClick={t3} disabled={disabled} ref={ref} tabIndex={0} type="button" data-destructive={destructive} {...props}>{t5}</button>;
    $[27] = ariaLabel;
    $[28] = destructive;
    $[29] = disabled;
    $[30] = props;
    $[31] = ref;
    $[32] = t2;
    $[33] = t3;
    $[34] = t5;
    $[35] = t6;
  } else {
    t6 = $[35];
  }
  return t6;
});
export default IconButton;